import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { RosterResponse } from '../types/community'
import { getRoster } from '../lib/api/circles'
import { usePerson } from '../contexts/PersonContext'

export type RosterSort = 'online' | 'rep' | 'played' | 'new'
export type RosterFilter = 'all' | 'online' | 'trusted' | 'played' | 'watched'

const INITIAL_TAKE = 8
const PAGE_STEP = 24

/**
 * Roster state machine for a gaming circle (COTC_Circles R4). Holds query / sort / filter and a
 * growing page size ("Show 24 more"). Changing query/sort/filter resets the page to 8. The server
 * pages by take; each grow refetches a larger single page (dc.html list semantics).
 */
export function useRoster(circleId: string | undefined) {
  const { activePerson } = usePerson()
  const [query, setQueryRaw] = useState('')
  const [sort, setSortRaw] = useState<RosterSort>('online')
  const [filter, setFilterRaw] = useState<RosterFilter>('all')
  const [take, setTake] = useState(INITIAL_TAKE)

  const result = useQuery<RosterResponse>({
    queryKey: ['community', 'roster', circleId, activePerson?.id, query, sort, filter, take],
    queryFn: () => getRoster(circleId as string, { query, sort, filter, skip: 0, take }),
    enabled: !!circleId,
    retry: false,
  })

  const setQuery = (v: string) => {
    setQueryRaw(v)
    setTake(INITIAL_TAKE)
  }
  const setSort = (v: RosterSort) => {
    setSortRaw(v)
    setTake(INITIAL_TAKE)
  }
  const setFilter = (v: RosterFilter) => {
    setFilterRaw(v)
    setTake(INITIAL_TAKE)
  }

  const data = result.data
  const entries = data?.entries ?? []
  const matching = data?.matching ?? 0
  const total = data?.total ?? 0
  const hasMore = entries.length < matching
  const showMore = () => {
    if (hasMore) setTake((t) => t + PAGE_STEP)
  }

  const showingText = useMemo(() => {
    const suffix = query.trim() !== '' || filter !== 'all' ? ' matching' : ''
    return `Showing ${entries.length} of ${matching}${suffix} members`
  }, [entries.length, matching, query, filter])

  return {
    query,
    sort,
    filter,
    setQuery,
    setSort,
    setFilter,
    stats: data?.stats,
    entries,
    total,
    matching,
    hasMore,
    showMore,
    showingText,
    moreLabel: hasMore ? 'Show 24 more' : 'End of list',
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    refetch: result.refetch,
  }
}
